import type { FC } from 'react';

import type { CardPresentation } from '@/features/storefront/components/ProductCard';
import type { FooterProps, HeaderProps } from '@/features/storefront/layouts/chrome/types';
import type { ProductViewProps } from '@/features/storefront/layouts/product/types';
import type { PartPresentation } from '@/features/storefront/parts/style';
import type { PageId } from '@/features/storefront/sections/pages';
import type {
  AnchorSectionType,
  PagePart,
  PartType,
} from '@/features/storefront/sections/parts';
import type { Section, SectionType } from '@/features/storefront/sections/schema';

/**
 * What a theme is, and what a theme may change.
 *
 * A theme is a folder under `previewThemes/` holding a `tokens.ts` and a
 * `theme.tsx`. The registry discovers it by folder name - nothing else in the
 * app is edited to add one - and every slot below is optional: a theme
 * overrides the parts it has a design for and inherits the rest from the base
 * theme. That is what keeps a new theme a folder rather than a fork of the
 * storefront.
 *
 * The page-shaped slots are *specs*, not components, wherever the page's logic
 * is identical across themes. A catalog's params, queries and pagination do
 * not change because the grid got denser, and six copies of that logic is six
 * places a pagination bug has to be fixed. Where a theme genuinely needs a
 * different arrangement rather than a different grid - the product page, the
 * header, the footer - it ships a component.
 */

/** How a theme fills its main content column. */
export interface ListingSpec {
  /** The catalog grid's Tailwind classes. */
  grid: string;
  /** Where the category filter stands. */
  filters: 'chips' | 'sidebar' | 'inline';
  /**
   * The widest the grid gets, repeated from `grid` because a class string is
   * not something the image code can read a column count out of.
   */
  columns: number;
  /** Which card the catalog's tiles wear. */
  card: CardPresentation;
}

/** The look of a summary or facet panel standing beside a page's main column. */
export type PanelStyle = 'filled' | 'outlined' | 'bare';

/** The cart: a list of lines with a totals panel beside it. */
export interface CartSpec {
  /** The lines-plus-summary grid. */
  grid: string;
  /** How much of that grid the lines take. */
  listSpan: string;
  panel: PanelStyle;
}

/** Checkout: the form, with the order summary beside it. */
export interface CheckoutSpec {
  /** The form-plus-summary grid. */
  grid: string;
  panel: PanelStyle;
}

/** Search: a facet rail and a grid of results. */
export interface SearchSpec {
  /** The rail-plus-results grid. */
  grid: string;
  /** The results grid itself. */
  results: string;
  columns: number;
  card: CardPresentation;
  panel: PanelStyle;
}

/** The blog index: how many posts stand beside each other, and how loudly. */
export interface BlogSpec {
  /** The index's list or grid. */
  list: string;
  /** The headline's scale on a post card. */
  title: string;
}

/** The category index, and the header a filtered catalog wears. */
export interface CategorySpec {
  /** The grid of category tiles. */
  grid: string;
  /** How one category tile presents itself. */
  tile: 'card' | 'overlay' | 'bare';
}

/**
 * The base values every theme starts from - today's storefront, exactly as it
 * rendered before themes were packages. A theme that declares none of these
 * renders the shop the way it has always rendered.
 */
export const BASE_LISTING: ListingSpec = {
  grid: 'grid-cols-2 gap-5 lg:grid-cols-4',
  filters: 'chips',
  columns: 4,
  card: 'framed',
};

export const BASE_CART: CartSpec = {
  grid: 'mt-8 grid gap-10 lg:grid-cols-3',
  listSpan: 'lg:col-span-2',
  panel: 'filled',
};

export const BASE_CHECKOUT: CheckoutSpec = {
  grid: 'mt-10 grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_24rem] lg:gap-14',
  panel: 'filled',
};

export const BASE_SEARCH: SearchSpec = {
  grid: 'mt-8 grid gap-10 lg:grid-cols-[220px_1fr]',
  results: 'grid grid-cols-2 gap-6 lg:grid-cols-3',
  columns: 3,
  card: 'framed',
  panel: 'bare',
};

export const BASE_BLOG: BlogSpec = {
  list: 'grid gap-8 sm:grid-cols-2 lg:grid-cols-3',
  title: 'text-lg',
};

export const BASE_CATEGORY: CategorySpec = {
  grid: 'grid gap-5 sm:grid-cols-2 lg:grid-cols-4',
  tile: 'card',
};

/**
 * Whether the storefront hangs its own two recommendation rails - similar
 * products, and what else sold alongside - under a theme's product view.
 *
 * True for every theme, and now a compatibility setting rather than a design
 * one: the rails are the `product_related` component, which a theme puts in
 * whichever region it wants and a merchant moves, hides or deletes like any
 * other. What this still does is keep them out of the *defaults* for a shop
 * that turned them off before they were a component - see `resolveProductRails`
 * and the `omit` argument to `anchorParts`.
 */
export const BASE_PRODUCT_RAILS = true;

/**
 * What one theme says about one customisable page.
 *
 * Both halves are optional and both are declarations rather than markup: a
 * theme says which sections belong on a page and what the page starts as, and
 * the builder and the storefront read the same answer. A theme that says
 * nothing gets the shop's own answer - every section offered, and the page's
 * anchor alone - which is what every theme in this build gets today.
 */
export interface ThemePageSpec {
  /**
   * The section types this theme offers on the page, in palette order.
   *
   * Left off means "all of them". Naming them is how a theme whose product
   * page is a tight, art-directed column keeps a slideshow from being dropped
   * into the middle of it. Anchors are never listed - a page owns exactly the
   * one it was born with, and the builder will not add a second.
   */
  available?: readonly SectionType[];
  /**
   * What the page renders for a merchant who has never arranged it.
   *
   * A function rather than an array because sections carry generated ids: two
   * merchants opening the same theme must not share one. Must contain the
   * page's anchor; `withAnchor` puts it back if it does not.
   */
  defaults?: () => Section[];
}

/**
 * A section this theme offers pre-filled, under a name of its own.
 *
 * This is how a theme gets a component library of its own without the editor
 * growing a component library per theme. A "Menu showcase", a "Chef's special",
 * a "Lookbook" are not new kinds of section - each is one of the shop's own
 * sections, arranged and worded the way this theme's design intends it, and
 * built by a function that returns exactly that section. The engine adds them,
 * moves them, inspects them and renders them through the same code path as
 * everything else, which is what keeps a new theme a folder rather than a
 * branch of the customiser.
 *
 * A preset that builds a type the theme has left off its page `available` list
 * is not offered there - one declaration decides both.
 */
export interface SectionPreset {
  /** Stable within a theme; used as a React key and in the search index. */
  id: string;
  label: string;
  /** What it is for, in the words a merchant would use. */
  description: string;
  /**
   * The section type it builds, declared rather than derived so the library
   * can filter a page's offering without constructing every preset in the
   * theme to look at what came out.
   */
  type: SectionType;
  /**
   * The pages it belongs on. Left off means every page - which is right for a
   * promo band and wrong for a "recently viewed" rail, so most name theirs.
   */
  pages?: readonly PageId[];
  /**
   * The section itself, built fresh on every use: two merchants adding the
   * same preset must not share an element id.
   */
  build: () => Section;
}

/**
 * One theme's markup and arrangement.
 *
 * Its id is not declared here - it is the folder name, so a theme cannot claim
 * an id that disagrees with where it lives. Its tokens and its name are not
 * here either: those live in the same folder's `meta.ts`, which is the half of
 * a theme that carries no React.
 */
/**
 * What one theme says about the components inside one page.
 *
 * The page-shaped slots above describe how a page *looks* - its grid, its
 * panels - and `ThemePageSpec` describes what may stand around it. This one
 * describes what may stand *inside* it, and it is the same three questions
 * asked one level down: which components this theme offers, what the page
 * starts as, and how the components it draws should look.
 *
 * Every field is optional and a theme that says nothing gets the shop's own
 * answer, which is the arrangement the storefront has always rendered. That is
 * not a fallback - it is what all nine themes in this build use today, because
 * their product pages differ in *layout*, which is `Product`, not in *what is
 * on them*, which is this.
 */
export interface ThemeAnchorParts {
  /**
   * The regions this theme actually draws, where it does not draw them all.
   *
   * Every theme in this build leaves this off, and that is the intended state:
   * themes arrange the same regions differently, which is what `Product` and
   * the spec slots are for. It exists for a theme that genuinely paints one of
   * a page's columns itself, and for that theme the honest answer is a shorter
   * tree rather than a tree full of rows that move nothing.
   *
   * Reach for it last. Hyper's product page used to be the one theme that
   * needed it - a counter and a magazine it drew itself - and turning that page
   * into sections and components was worth more than the shortcut was: naming a
   * region here takes a whole column away from the merchant.
   *
   * A component whose region is not drawn is not offered and not defaulted, so
   * the editor never shows a merchant a control that does nothing.
   */
  regions?: readonly string[];
  /**
   * The component types this theme offers on the page, in library order.
   *
   * Left off means "all of them". Naming them is how a theme whose product page
   * is a tight art-directed column keeps a quantity stepper and a second button
   * out of the middle of it. A component the page cannot work without is never
   * excluded by this - see `PartSpec.required`.
   */
  available?: readonly PartType[];
  /**
   * What the page is made of for a merchant who has never arranged it.
   *
   * A function rather than an array because parts carry generated ids: two
   * merchants opening the same theme must not share one. Must contain the
   * page's required parts; `withRequired` puts back any it does not.
   */
  defaults?: () => PagePart[];
  /**
   * How this theme's components look while the merchant has not restyled them.
   *
   * Where a theme keeps a design it already has - Editorial's `text-4xl`
   * product title, Serra's portrait gallery with a rail of thumbnails - without
   * writing out a whole default arrangement to hold two classes. See
   * `PartPresentation`.
   */
  presentation?: PartPresentation;
}

export interface ThemePackage {
  Header?: FC<HeaderProps>;
  Footer?: FC<FooterProps>;
  Product?: FC<ProductViewProps>;
  /** See {@link BASE_PRODUCT_RAILS}. Left off means the shared rails render. */
  productRails?: boolean;
  /**
   * Whether the header's cart link, and "Add to cart" on a product page, open
   * the quick cart - a drawer that slides in from the right - rather than
   * going to the cart page. Left off means the cart page, as every theme did
   * before the drawer existed. See `features/storefront/components/QuickCart`.
   *
   * This is only the *default*: the quick cart is a plugin, and a merchant can
   * switch it on or off for any theme (`features/storefront/plugins`).
   */
  quickCart?: boolean;

  /**
   * What this theme offers on each customisable page, and what each starts as.
   *
   * The spec slots below describe how a page *looks* - its grid, its panels -
   * and this one describes what may stand around it. They are separate because
   * they answer to different people: the spec is the designer's, and the page
   * is the merchant's to fill.
   */
  pages?: Partial<Record<PageId, ThemePageSpec>>;

  /**
   * The sections this theme offers pre-arranged, listed in the "add section"
   * library above the shop's own. See {@link SectionPreset}.
   */
  presets?: readonly SectionPreset[];

  /**
   * What this theme says about the components inside each page. See
   * {@link ThemeAnchorParts}.
   */
  parts?: Partial<Record<AnchorSectionType, ThemeAnchorParts>>;

  listing?: Partial<ListingSpec>;
  cart?: Partial<CartSpec>;
  checkout?: Partial<CheckoutSpec>;
  search?: Partial<SearchSpec>;
  blog?: Partial<BlogSpec>;
  category?: Partial<CategorySpec>;
}

/** A theme as the registry hands it back: its folder name included. */
export type RegisteredTheme = ThemePackage & { id: string };
